import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiAward, FiPlus, FiShield, FiUser, FiUsers } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { PRIMARY } from '../../theme';
import { useReferenceData } from '../../contexts/ReferenceDataContext';
import { PERMISSIONS, buildRoleFeatureMatrix, canFeatureForRole } from '../../utils/permissions';
import ConfirmActionModal from '../../components/modals/ConfirmActionModal';
import StyledSelectDropdown from '../../components/common/StyledSelectDropdown';
import {
  getRoleIconComponent,
  TEAM_MEMBER_MODAL_ACCENT,
} from '../../utils/teamMemberDetailModalUi';

const toDateOnly = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
};

/** Monday–Sunday bounds in local time, aligned with TeamsEventsPage "This Week". */
const getCurrentWeekLocalBounds = () => {
  const now = new Date();
  const weekStart = new Date(now);
  const dayOfWeek = now.getDay();
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  weekStart.setDate(now.getDate() + diffToMonday);
  weekStart.setHours(0, 0, 0, 0);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  weekEnd.setHours(0, 0, 0, 0);
  return { weekStart, weekEnd };
};

const toLocalYmd = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const toInputDateValue = (value) => {
  if (!value) return '';
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
};

const toInputTimeValue = (value) => {
  if (!value) return '';
  const raw = String(value).trim();
  if (/^\d{2}:\d{2}$/.test(raw)) return raw;

  const amPmMatch = raw.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
  if (amPmMatch) {
    const [, hourText, minute, periodText] = amPmMatch;
    let hour = Number(hourText);
    const period = periodText.toUpperCase();
    if (period === 'PM' && hour < 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;
    return `${String(hour).padStart(2, '0')}:${minute}`;
  }

  const parsed = new Date(`1970-01-01T${raw}`);
  if (Number.isNaN(parsed.getTime())) return '';
  return `${String(parsed.getHours()).padStart(2, '0')}:${String(
    parsed.getMinutes()
  ).padStart(2, '0')}`;
};

/** Matches API shapes used in TeamsEventsPage / EventFeedbackPage (nested employee/user/member). */
const extractPersonIdFromMemberRow = (m) => {
  if (m == null) return '';
  if (typeof m !== 'object') {
    const s = String(m).trim();
    return s && s !== '[object Object]' ? s : '';
  }

  const candidates = [
    m.userId,
    m.memberId,
    m.employeeId,
    m.id,
    m._id,
    m.employee?._id,
    m.employee?.id,
    m.member?._id,
    m.member?.id,
    m.user?._id,
    m.user?.id,
  ];

  for (const c of candidates) {
    if (c == null || c === '') continue;
    const s = String(c).trim();
    if (s && s !== 'undefined' && s !== 'null' && s !== '[object Object]') return s;
  }
  return '';
};

const collectAssignedUserIdsFromApiEvent = (item) => {
  const ids = new Set();
  const leaderId = String(
    item?.teamLeaderId ||
      item?.teamLeader?._id ||
      item?.teamLeader?.id ||
      (typeof item?.teamLeader === 'string' ? item.teamLeader : '') ||
      ''
  ).trim();
  if (leaderId) ids.add(leaderId);
  const members = Array.isArray(item?.teamMembers)
    ? item.teamMembers
    : Array.isArray(item?.members)
    ? item.members
    : [];
  for (const m of members) {
    const uid = extractPersonIdFromMemberRow(m);
    if (uid) ids.add(uid);
  }
  return ids;
};

/** Normalizes start/end date + time into comparable local Date instances. */
const normalizeEventScheduleSlot = (startDate, endDate, startTime, endTime) => {
  const sd = toInputDateValue(startDate);
  const ed = toInputDateValue(endDate);
  const st = toInputTimeValue(startTime);
  const et = toInputTimeValue(endTime);
  if (!sd || !ed || !st || !et) return null;
  const start = new Date(`${sd}T${st}:00`);
  const end = new Date(`${ed}T${et}:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return { sd, ed, st, et, start, end };
};

const eventScheduleSlotsOverlap = (a, b) => {
  if (!a || !b) return false;
  if (!(a.start instanceof Date) || !(a.end instanceof Date)) return false;
  if (!(b.start instanceof Date) || !(b.end instanceof Date)) return false;
  return a.start < b.end && a.end > b.start;
};

const normalizeJobType = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[\s/_-]/g, '');

/** Align with TeamsEventsPage toolbar filters (`filterFieldShell` / `filterSelectStyle`). */
const TEAMS_PAGE_TOOLBAR_BORDER = '#dee2e6';


/** Match TeamsEventsPage `toolbarPrimaryBtnBase` + `Add Team` (PRIMARY gradient from theme). */
const ADD_TEAM_FORM_PRIMARY_BTN = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  flexShrink: 0,
  height: 34,
  padding: '0 14px',
  border: 'none',
  color: '#ffffff',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  boxShadow: '0 1px 2px rgba(78, 115, 223, 0.28)',
};

/** Match TeamsEventsPage secondary toolbar controls (e.g. Clear Filters). */
const ADD_TEAM_FORM_CANCEL_BTN = {
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  height: 34,
  padding: '0 14px',
  border: `1px solid ${TEAMS_PAGE_TOOLBAR_BORDER}`,
  background: '#ffffff',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  color: '#6c757d',
  boxSizing: 'border-box',
};

const roundToTenthWith85Threshold = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;

  const scaled = n * 10;
  const lower = Math.floor(scaled) / 10;
  const remainder = scaled - Math.floor(scaled);

  // Round up to the next tenth only at x.x5 on the scaled value (e.g. 4.85+ => 4.9).
  return remainder >= 0.5
    ? Number((lower + 0.1).toFixed(1))
    : Number(lower.toFixed(1));
};

const getEmployeeFeedbackPayload = (response) => response?.data?.data || response?.data || response || {};

const extractEmployeeSummaryRows = (response) => {
  const payload = getEmployeeFeedbackPayload(response);
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.summary)) return payload.summary;
  if (Array.isArray(payload.items)) return payload.items;
  return [];
};

const getCandidateUserIds = (user) => {
  if (!user || typeof user !== 'object') return [];
  const rawCandidates = [
    user.id,
    user._id,
    user.userId,
    user.memberId,
    user.employeeId,
    user.employee?._id,
    user.employee?.id,
    user.member?._id,
    user.member?.id,
    user.user?._id,
    user.user?.id,
  ];
  const unique = new Set();
  rawCandidates.forEach((candidate) => {
    if (candidate === null || candidate === undefined) return;
    const normalized = String(candidate).trim();
    if (normalized) unique.add(normalized);
  });
  return Array.from(unique);
};

const resolveFeedbackRoleLabel = (row) => {
  const fromRow =
    row?.eventJobType ||
    row?.eventJobTypeName ||
    row?.eventRole ||
    row?.eventRoleName ||
    row?.assignedRole ||
    row?.teamRole ||
    row?.feedbackForRole ||
    row?.jobTypeName ||
    row?.jobType ||
    row?.role ||
    row?.title ||
    row?.event?.jobTypeName ||
    row?.event?.jobType?.name ||
    row?.event?.jobType ||
    row?.event?.role?.name ||
    row?.event?.role ||
    '';
  return String(fromRow || '').trim();
};

const resolveFeedbackEmployeeId = (row) =>
  String(
    row?.employeeId || row?.userId || row?.memberId || row?.employee?._id || row?.employee?.id || ''
  ).trim();

const resolveFeedbackRatingValue = (row) => {
  const fromRow = Number(
    row?.overallRating ?? row?.averageRating ?? row?.avgRating ?? row?.total ?? row?.totalScore
  );
  return Number.isFinite(fromRow) ? fromRow : null;
};

const buildRoleAwareOverallRatingsMap = (response) => {
  const roleStats = {};
  const upsert = (roleName, employeeId, ratingValue, eventCount = null) => {
    const normalizedRole = normalizeJobType(roleName);
    if (!normalizedRole || !employeeId) return;
    const key = `${normalizedRole}:${employeeId}`;
    const existing = roleStats[key] || { rating: null, totalEvents: 0 };

    if (Number.isFinite(ratingValue) && !Number.isFinite(existing.rating)) {
      existing.rating = ratingValue;
    }
    if (
      Number(existing.totalEvents || 0) <= 0 &&
      Number.isFinite(Number(eventCount)) &&
      Number(eventCount) > 0
    ) {
      existing.totalEvents = Number(eventCount);
    }

    roleStats[key] = existing;
  };

  // Add Team should use only summary rows from employee summary endpoint.
  extractEmployeeSummaryRows(response).forEach((row) => {
    const employeeId = resolveFeedbackEmployeeId(row);
    const roleName = resolveFeedbackRoleLabel(row);
    const ratingValue = resolveFeedbackRatingValue(row);
    const totalEvents = Number(row?.totalEvents);
    if (!employeeId) return;
    upsert(roleName, employeeId, ratingValue, totalEvents);
  });

  return roleStats;
};

const AddTeamEventPage = ({ variant = 'add' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const appBasePath = useMemo(() => {
    try {
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      return user?.role === 'admin' ? '/admin' : '/employee';
    } catch {
      return '/employee';
    }
  }, []);
  const { jobTypeRows: rawJobTypeRows } = useReferenceData();
  const jobTypes = useMemo(() => {
    const rows = rawJobTypeRows || [];
    return rows
      .map((item) => {
        if (typeof item === 'string') return { id: '', name: item };
        return {
          id: String(item?._id || item?.id || ''),
          name: String(item?.name || item?.jobType || item?.title || ''),
        };
      })
      .filter((item) => item.name);
  }, [rawJobTypeRows]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [overallRatingsByRoleAndEmployeeId, setOverallRatingsByRoleAndEmployeeId] = useState(
    {}
  );
  const [existingEventTeams, setExistingEventTeams] = useState([]);
  const [showDuplicateMemberWarning, setShowDuplicateMemberWarning] = useState(false);
  const [duplicateRoleAssignments, setDuplicateRoleAssignments] = useState([]);
  const [showScheduleConflictWarning, setShowScheduleConflictWarning] = useState(false);
  const [scheduleConflictModal, setScheduleConflictModal] = useState({
    message: '',
    /** null = informational (member pick); true = user chose Save with conflicts */
    pendingPayload: null,
  });
  const [rosterLockedByStartedMatch, setRosterLockedByStartedMatch] = useState(false);
  const [rosterLockCheckLoading, setRosterLockCheckLoading] = useState(() => variant === 'edit');

  const roleRows = useMemo(
    () =>
      (jobTypes || []).filter(
        (jobType) => normalizeJobType(jobType.name) !== normalizeJobType('Team Leader')
      ),
    [jobTypes]
  );

  const [formData, setFormData] = useState({
    teamName: '',
    teamLeader: '',
  });

  const editEventTeam = location.state?.editEventTeam;
  const isEditMode = variant === 'edit';

  const rosterControlsLocked =
    rosterLockedByStartedMatch || (isEditMode && rosterLockCheckLoading);

  useEffect(() => {
    if (!isEditMode || !editEventTeam) {
      setRosterLockedByStartedMatch(false);
      setRosterLockCheckLoading(false);
      return undefined;
    }

    const eventTeamId = String(editEventTeam?.eventTeamId || editEventTeam?.id || '').trim();
    if (!eventTeamId) {
      setRosterLockCheckLoading(false);
      return undefined;
    }

    // Roster lock is enforced by backend update API using latest match/feedback state.
    // Keep UI editable and let server return rule-specific error when lock applies.
    setRosterLockedByStartedMatch(false);
    setRosterLockCheckLoading(false);
    return undefined;
  }, [isEditMode, editEventTeam?.eventTeamId, editEventTeam?.id]);

  const isTeamNameTaken = useCallback(
    (rawName, excludeEventTeamId = '') => {
      const normalized = String(rawName || '').trim().toLowerCase();
      if (!normalized) return false;
      const exclude = String(excludeEventTeamId || '').trim();
      return existingEventTeams.some((item) => {
        const id = String(item?._id || item?.id || item?.eventTeamId || '').trim();
        if (exclude && id === exclude) return false;
        const teamName = String(item?.teamName || '').trim().toLowerCase();
        return teamName === normalized;
      });
    },
    [existingEventTeams]
  );

  useEffect(() => {
    if (variant !== 'edit') return;
    if (location.state?.editEventTeam) return;
    let user = {};
    try {
      user = JSON.parse(sessionStorage.getItem('user') || '{}');
    } catch {
      user = {};
    }
    const base = user?.role === 'admin' ? '/admin' : '/employee';
    toast.error('Open edit from the Teams list.');
    navigate(`${base}/teams-events`, { replace: true });
  }, [variant, navigate, location.state]);

  useEffect(() => {
    if (variant !== 'copy') return;
    if (location.state?.eventTeam) return;
    let user = {};
    try {
      user = JSON.parse(sessionStorage.getItem('user') || '{}');
    } catch {
      user = {};
    }
    const base = user?.role === 'admin' ? '/admin' : '/employee';
    toast.error('Open copy from the Teams list.');
    navigate(`${base}/teams-events`, { replace: true });
  }, [variant, navigate, location.state]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      let user = {};
      try {
        user = JSON.parse(sessionStorage.getItem('user') || '{}');
      } catch {
        user = {};
      }
      const base = user?.role === 'admin' ? '/admin' : '/employee';
      const role = user?.role;

      let matrix = {};
      try {
        const response = await api.getPermissions();
        const permissionRows = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.permissions)
          ? response.data.permissions
          : Array.isArray(response?.permissions)
          ? response.permissions
          : [];
        matrix = buildRoleFeatureMatrix(permissionRows);
      } catch {
        matrix = {};
      }
      if (cancelled) return;
      const allowed = isEditMode
        ? canFeatureForRole(role, matrix, PERMISSIONS.EDIT_TEAM)
        : variant === 'copy'
        ? canFeatureForRole(role, matrix, PERMISSIONS.ADD_TEAM)
        : canFeatureForRole(role, matrix, PERMISSIONS.ADD_TEAM);
      if (!allowed) {
        toast.error(
          isEditMode
            ? 'You do not have permission to edit teams or events'
            : variant === 'copy'
            ? 'You do not have permission to copy teams or events'
            : 'You do not have permission to add teams or events'
        );
        navigate(`${base}/teams-events`, { replace: true });
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [isEditMode, navigate, variant]);

  useEffect(() => {
    const fetchUsersAndJobTypes = async () => {
      try {
        setLoadingUsers(true);

        const usersResponse = await api.getUsers({ isActive: 'true', limit: 1000 });

        const userRows = Array.isArray(usersResponse?.data)
          ? usersResponse.data
          : Array.isArray(usersResponse?.data?.users)
          ? usersResponse.data.users
          : Array.isArray(usersResponse?.users)
          ? usersResponse.users
          : [];
        setUsers(userRows);
      } catch (error) {
        console.error('Error loading users/job types:', error);
        toast.error('Unable to load users or job types');
      } finally {
        setLoadingUsers(false);
      }

      // Load event names from event teams — failures here are non-critical
      try {
        const [allEventTeamsResponse, summaryResponse] = await Promise.all([
          api.getEventTeams(),
          api.getEmployeeFeedbackSummary({ includeDetails: false }).catch(() => null),
        ]);

        const allEventRows = Array.isArray(allEventTeamsResponse?.data)
          ? allEventTeamsResponse.data
          : Array.isArray(allEventTeamsResponse)
          ? allEventTeamsResponse
          : [];

        setExistingEventTeams(allEventRows);
        if (summaryResponse) {
          setOverallRatingsByRoleAndEmployeeId(buildRoleAwareOverallRatingsMap(summaryResponse));
        }
      } catch (error) {
        console.error('Error loading event teams:', error);
      }
    };

    fetchUsersAndJobTypes();
  }, []);

  useEffect(() => {
    setTeamMembers((prev) =>
      roleRows.map((roleObj) => {
        const existing = prev.find((item) => item.roleId === roleObj.id || item.role === roleObj.name);
        return {
          roleId: roleObj.id,
          role: roleObj.name,
          memberId: existing?.memberId || '',
          showAllEmployees: existing?.showAllEmployees || false,
        };
      })
    );
  }, [roleRows]);

  const employeeUsers = useMemo(
    () =>
      users.filter(
        (user) => String(user.role || '').trim().toLowerCase() === 'employee'
      ),
    [users]
  );

  const employeeAndAdminUsers = useMemo(
    () =>
      users.filter((user) => {
        const role = String(user.role || '').trim().toLowerCase();
        return role === 'employee' || role === 'admin';
      }),
    [users]
  );

  const getUserValue = (user) => user.id || user._id || user.userId || '';

  const getUserLabel = (user) =>
    user.name ||
    user.fullName ||
    user.userName ||
    user.username ||
    user.email ||
    user.employeeId ||
    'Unnamed User';

  const getUserRoleStats = (user, roleName) => {
    if (!user || typeof user !== 'object') return null;

    const roleKey = normalizeJobType(roleName);
    if (!roleKey) return null;

    const candidateIds = getCandidateUserIds(user);
    if (!candidateIds.length) return null;

    const mappedStats = candidateIds
      .map((candidateId) => overallRatingsByRoleAndEmployeeId[`${roleKey}:${candidateId}`])
      .find((stats) => stats && typeof stats === 'object');

    if (!mappedStats) return null;

    return {
      rating: Number.isFinite(Number(mappedStats.rating))
        ? Number(mappedStats.rating)
        : null,
      totalEvents: Number.isFinite(Number(mappedStats.totalEvents))
        ? Math.max(0, Number(mappedStats.totalEvents))
        : 0,
    };
  };

  const getMemberLabelWithRating = (user, roleName) => {
    const name = getUserLabel(user);
    const stats = getUserRoleStats(user, roleName);
    if (!stats || stats.rating === null) return name;
    const eventsLabel = `${stats.totalEvents} ${stats.totalEvents === 1 ? 'match' : 'matches'}`;
    const displayRating = roundToTenthWith85Threshold(stats.rating);
    return `${name} (${displayRating.toFixed(1)}★, ${eventsLabel})`;
  };

  const teamLeaderUsers = useMemo(() => {
    const filtered = users.filter((user) => {
      const normalizedRole = normalizeJobType(user?.role);
      return normalizedRole === normalizeJobType('Team Leader');
    });
    return [...filtered].sort((a, b) => {
      const aStats = getUserRoleStats(a, 'Team Leader');
      const bStats = getUserRoleStats(b, 'Team Leader');
      const aRating = aStats?.rating;
      const bRating = bStats?.rating;
      const aHasRating = Number.isFinite(aRating);
      const bHasRating = Number.isFinite(bRating);
      if (aHasRating && bHasRating && aRating !== bRating) {
        return bRating - aRating;
      }
      if (aHasRating && bHasRating) {
        const aEvents = Number(aStats?.totalEvents || 0);
        const bEvents = Number(bStats?.totalEvents || 0);
        if (aEvents !== bEvents) return bEvents - aEvents;
      }
      if (aHasRating && !bHasRating) return -1;
      if (!aHasRating && bHasRating) return 1;
      return getUserLabel(a).localeCompare(getUserLabel(b));
    });
  }, [users, overallRatingsByRoleAndEmployeeId]);

  const getUserById = useCallback(
    (userId) => users.find((user) => String(getUserValue(user)) === String(userId)),
    [users]
  );

  const getMembersForRole = (roleName, includeAllEmployees = false) => {
    const roleKey = normalizeJobType(roleName);
    const roleMembers = includeAllEmployees
      ? employeeAndAdminUsers
      : employeeUsers.filter((user) => {
          const userJobType = user.jobType || user.title || '';
          return normalizeJobType(userJobType) === roleKey;
        });

    return [...roleMembers].sort((a, b) => {
      const aStats = getUserRoleStats(a, roleName);
      const bStats = getUserRoleStats(b, roleName);
      const aRating = aStats?.rating;
      const bRating = bStats?.rating;

      const aHasRating = Number.isFinite(aRating);
      const bHasRating = Number.isFinite(bRating);

      if (aHasRating && bHasRating && aRating !== bRating) {
        return bRating - aRating;
      }
      if (aHasRating && bHasRating) {
        const aEvents = Number(aStats?.totalEvents || 0);
        const bEvents = Number(bStats?.totalEvents || 0);
        if (aEvents !== bEvents) return bEvents - aEvents;
      }
      if (aHasRating && !bHasRating) return -1;
      if (!aHasRating && bHasRating) return 1;

      return getUserLabel(a).localeCompare(getUserLabel(b));
    });
  };

  const resolveUserId = useCallback(
    (candidate) => {
      if (!candidate) return '';
      const rawId =
        (typeof candidate === 'object' &&
          (candidate.id || candidate._id || candidate.userId || candidate.memberId)) ||
        (typeof candidate === 'string' ? candidate : '');

      if (rawId) {
        const byId = users.find(
          (user) => String(getUserValue(user)) === String(rawId)
        );
        if (byId) return String(getUserValue(byId));
      }

      const candidateText = String(candidate).trim().toLowerCase();
      const byName = users.find(
        (user) => getUserLabel(user).trim().toLowerCase() === candidateText
      );
      return byName ? String(getUserValue(byName)) : '';
    },
    [users]
  );

  useEffect(() => {
    if (variant !== 'copy') return;
    const sourceEventTeam = location.state?.eventTeam;
    const editSource = location.state?.editEventTeam;
    if (!sourceEventTeam || editSource || users.length === 0) return;

    const resolvedLeaderId = resolveUserId(
      sourceEventTeam.teamLeaderId ||
        sourceEventTeam.teamLeader ||
        sourceEventTeam.teamLeaderName
    );

    setFormData((prev) => ({
      ...prev,
      teamName: '',
      teamLeader: resolvedLeaderId,
    }));

    const members = Array.isArray(sourceEventTeam.teamMembers)
      ? sourceEventTeam.teamMembers
      : Array.isArray(sourceEventTeam.members)
      ? sourceEventTeam.members
      : [];

    if (members.length === 0) return;

    setTeamMembers((prev) => {
      const rowsToPopulate =
        prev.length > 0
          ? prev
          : roleRows.map((roleObj) => ({
              roleId: roleObj.id,
              role: roleObj.name,
              memberId: '',
              showAllEmployees: false,
            }));

      return rowsToPopulate.map((row) => {
        const assigned = members.find(
          (item) =>
            normalizeJobType(item?.role?.name || item?.role) === normalizeJobType(row.role)
        );
        const memberId = resolveUserId(
          assigned?.member ||
            assigned?.userId ||
            assigned?.memberId ||
            assigned?.employeeId ||
            assigned?.user ||
            assigned?.employee
        );
        const roleKey = normalizeJobType(row.role);
        const existsInRoleList = employeeUsers.some((user) => {
          const userId = String(getUserValue(user));
          const userRole = normalizeJobType(user.jobType || user.title || '');
          return userId === String(memberId) && userRole === roleKey;
        });
        return {
          ...row,
          memberId,
          showAllEmployees: Boolean(memberId && !existsInRoleList),
        };
      });
    });
  }, [location.state, users, employeeUsers, resolveUserId, roleRows, variant]);

  useEffect(() => {
    if (variant !== 'edit') return;
    const editSource = location.state?.editEventTeam;
    if (!editSource || users.length === 0) return;

    const editTeamName = String(editSource.teamName || editSource.eventName || '').trim();

    const resolvedLeaderId = resolveUserId(
      editSource.teamLeaderId ||
        editSource.teamLeader ||
        editSource.teamLeaderName
    );

    setFormData({
      teamName: editTeamName,
      teamLeader: resolvedLeaderId,
    });

    if (roleRows.length === 0) return;

    const members = Array.isArray(editSource.teamMembers)
      ? editSource.teamMembers
      : Array.isArray(editSource.members)
      ? editSource.members
      : [];

    setTeamMembers(() => {
      const rowsToPopulate = roleRows.map((roleObj) => ({
        roleId: roleObj.id,
        role: roleObj.name,
        memberId: '',
        showAllEmployees: false,
      }));

      return rowsToPopulate.map((row) => {
        const assigned = members.find(
          (item) =>
            normalizeJobType(item?.role?.name || item?.role) === normalizeJobType(row.role)
        );
        const memberId = resolveUserId(
          assigned?.member ||
            assigned?.userId ||
            assigned?.memberId ||
            assigned?.employeeId ||
            assigned?.user ||
            assigned?.employee
        );
        const roleKey = normalizeJobType(row.role);
        const existsInRoleList = employeeUsers.some((user) => {
          const userId = String(getUserValue(user));
          const userRole = normalizeJobType(user.jobType || user.title || '');
          return userId === String(memberId) && userRole === roleKey;
        });
        return {
          ...row,
          memberId,
          showAllEmployees: Boolean(memberId && !existsInRoleList),
        };
      });
    });
  }, [location.state?.editEventTeam, users, employeeUsers, resolveUserId, roleRows, variant]);

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMemberChange = (index, memberId) => {
    if (rosterLockedByStartedMatch) {
      toast.error(
        'Cannot change team members while this team is assigned to a match that has already started.'
      );
      return;
    }
    const nextMembers = teamMembers.map((row, rowIndex) =>
      rowIndex === index ? { ...row, memberId } : row
    );
    setTeamMembers(nextMembers);
  };

  const handleShowAllEmployeesChange = (index, checked) => {
    if (rosterLockedByStartedMatch) {
      toast.error(
        'Cannot change team members while this team is assigned to a match that has already started.'
      );
      return;
    }
    setTeamMembers((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, showAllEmployees: checked } : row
      )
    );
  };

  const buildPayload = () => {
    const assignedMembers = teamMembers
      .filter((item) => item.memberId)
      .map((item) => ({
        role: item.roleId,
        member: item.memberId,
      }));

    return {
      teamName: formData.teamName.trim(),
      teamLeader: formData.teamLeader,
      teamMembers: assignedMembers,
    };
  };

  const getDuplicateMemberAssignments = useCallback(() => {
    const assignmentsByMember = teamMembers.reduce((acc, row) => {
      if (!row.memberId) return acc;
      const key = String(row.memberId);
      if (!acc[key]) acc[key] = [];
      acc[key].push(row.role);
      return acc;
    }, {});

    return Object.entries(assignmentsByMember)
      .filter(([, roles]) => roles.length > 1)
      .map(([memberId, roles]) => ({
        memberId,
        roles,
      }));
  }, [teamMembers]);

  const computeScheduleConflicts = useCallback(
    (membersRows, form) => {
      const proposedSlot = normalizeEventScheduleSlot(
        form.startDate,
        form.endDate,
        form.startTime,
        form.endTime
      );
      if (!proposedSlot) return [];

      const excludeId = isEditMode
        ? String(editEventTeam?.eventTeamId || editEventTeam?.id || '').trim()
        : '';

      const proposedIds = new Set();
      if (form.teamLeader) proposedIds.add(String(form.teamLeader));
      for (const row of membersRows) {
        if (row.memberId) proposedIds.add(String(row.memberId));
      }

      const conflicts = [];
      for (const item of existingEventTeams) {
        const eventId = String(item?._id || item?.id || item?.eventTeamId || '').trim();
        if (excludeId && eventId && eventId === excludeId) continue;

        const otherSlot = normalizeEventScheduleSlot(
          item?.startDate,
          item?.endDate,
          item?.startTime,
          item?.endTime
        );
        if (!otherSlot) continue;
        if (!eventScheduleSlotsOverlap(proposedSlot, otherSlot)) continue;

        const otherIds = collectAssignedUserIdsFromApiEvent(item);
        const overlapIds = [...proposedIds].filter((id) => otherIds.has(id));
        if (overlapIds.length === 0) continue;

        const eventName = String(item?.eventName || item?.event || 'Another event').trim();
        const teamLabel = String(item?.teamName || item?.team || '').trim();
        const memberNames = overlapIds
          .map((id) => {
            const user = getUserById(id);
            return user ? getUserLabel(user) : id;
          })
          .join(', ');

        conflicts.push({ eventName, teamLabel, memberNames });
      }
      return conflicts;
    },
    [existingEventTeams, isEditMode, editEventTeam, getUserById]
  );

  const formatScheduleConflictMessage = (conflicts) => {
    const lines = conflicts.map((c) => {
      const team = c.teamLabel ? ` (${c.teamLabel})` : '';
      return `- ${c.eventName}${team}: ${c.memberNames}`;
    });
    const conflictLabel =
      conflicts.length === 1 ? '1 conflicting event found' : `${conflicts.length} conflicting events found`;
    return `Some selected people are already assigned to event(s) in an overlapping schedule window.\n\n${conflictLabel}:\n${lines.join(
      '\n'
    )}\n\nSelect "Go back" to review assignments, or "Save anyway" to continue.`;
  };

  const submitEventTeam = async (payload) => {
    try {
      setIsSubmitting(true);

      if (isEditMode) {
        const eventTeamId = editEventTeam?.eventTeamId || editEventTeam?.id;
        if (!eventTeamId) {
          toast.error('Event team id not found');
          return;
        }
        await api.updateEventTeam(eventTeamId, payload);
        toast.success('Event team updated successfully');
      } else {
        await api.createEventTeam(payload);
        toast.success('Event team created successfully');
      }
      navigate(`${appBasePath}/teams-events`);
    } catch (error) {
      console.error(isEditMode ? 'Update event team error:' : 'Create event team error:', error);
      toast.error(
        error.message ||
          (isEditMode ? 'Failed to update event team' : 'Failed to create event team')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDuplicateMemberWarning = async () => {
    setShowDuplicateMemberWarning(false);
    setDuplicateRoleAssignments([]);
    const payload = buildPayload();
    await submitEventTeam(payload);
  };

  const finalizeOrPromptDuplicates = async () => {
    const duplicateAssignments = getDuplicateMemberAssignments();
    if (duplicateAssignments.length > 0) {
      setDuplicateRoleAssignments(duplicateAssignments);
      setShowDuplicateMemberWarning(true);
      return;
    }
    await submitEventTeam(buildPayload());
  };

  const handleScheduleConflictClose = () => {
    setShowScheduleConflictWarning(false);
    setScheduleConflictModal({ message: '', pendingPayload: null });
  };

  const handleScheduleConflictConfirm = async () => {
    const { pendingPayload } = scheduleConflictModal;
    setShowScheduleConflictWarning(false);
    setScheduleConflictModal({ message: '', pendingPayload: null });
    if (pendingPayload) {
      await finalizeOrPromptDuplicates();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (
      !formData.teamName.trim() ||
      !formData.teamLeader
    ) {
      toast.error('Please fill all required fields');
      return;
    }

    const excludeId = isEditMode
      ? String(editEventTeam?.eventTeamId || editEventTeam?.id || '').trim()
      : '';
    if (isTeamNameTaken(formData.teamName, excludeId)) {
      toast.error('A team with this name already exists. Choose a different name.');
      return;
    }

    await finalizeOrPromptDuplicates();
  };

  return (
    <div style={{ padding: '8px 16px 24px', maxWidth: '920px', margin: '0 auto' }}>
      <div
        style={{
          background: '#ffffff',
          borderRadius: '16px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 4px 24px rgba(15, 23, 42, 0.06)',
          overflow: 'hidden',
        }}
      >
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <div
            style={{
              padding: '16px 20px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#ffffff',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                aria-hidden
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.22)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <FiUsers size={22} strokeWidth={2} />
              </div>
              <div>
                <div style={{ fontSize: '18px', fontWeight: 700, letterSpacing: '-0.02em' }}>Team Information</div>
                <div style={{ fontSize: '12px', fontWeight: 500, opacity: 0.92, marginTop: '4px' }}>
                  Assign members to roles and set team leader
                </div>
              </div>
            </div>
          </div>

          <div style={{ padding: '18px 20px', display: 'grid', gap: '18px' }}>
            {isEditMode && rosterLockCheckLoading && (
              <div
                style={{
                  padding: '10px 12px',
                  borderRadius: 8,
                  fontSize: 12,
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  color: '#475569',
                }}
              >
                Checking whether this team is assigned to a started match…
              </div>
            )}
            {isEditMode && rosterLockedByStartedMatch && !rosterLockCheckLoading && (
              <div
                style={{
                  padding: '10px 12px',
                  borderRadius: 8,
                  fontSize: 12,
                  background: '#fffbeb',
                  border: '1px solid #fcd34d',
                  color: '#92400e',
                }}
              >
                This team is assigned to a match that has already started. Team leader and role
                members cannot be changed until the team is not used on an active schedule (or only
                on matches that have not started yet). You can still update the team name.
              </div>
            )}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                gap: '12px',
                alignItems: 'center',
                width: '60%',
              }}
            >
              <div style={{ position: 'relative' }}>
                <FiUsers
                  size={14}
                  strokeWidth={2}
                  style={{
                    color: TEAM_MEMBER_MODAL_ACCENT,
                    position: 'absolute',
                    left: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                  }}
                  aria-hidden
                />
                <input
                  type="text"
                  name="teamName"
                  value={formData.teamName}
                  onChange={handleFieldChange}
                  placeholder="Enter Team Name"
                  required
                  style={{
                    width: '100%',
                    height: 34,
                    padding: '0 12px 0 32px',
                    background: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 500,
                    color: '#334155',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <StyledSelectDropdown
                placeholder="Select Team Leader"
                emptyOptionLabel={loadingUsers ? 'Loading users...' : 'Select Team Leader'}
                value={formData.teamLeader}
                onChange={(v) => {
                  if (rosterLockedByStartedMatch) {
                    toast.error(
                      'Cannot change team leader while this team is assigned to a match that has already started.'
                    );
                    return;
                  }
                  handleFieldChange({ target: { name: 'teamLeader', value: v } });
                }}
                options={teamLeaderUsers.map((user) => ({
                  value: getUserValue(user),
                  label: getMemberLabelWithRating(user, 'Team Leader'),
                }))}
                disabled={loadingUsers || rosterControlsLocked}
                isLoading={loadingUsers}
                loadingText="Loading users..."
                leftIcon={<FiAward size={14} strokeWidth={2} style={{ color: TEAM_MEMBER_MODAL_ACCENT, flexShrink: 0 }} aria-hidden />}
              />
            </div>

            <div
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                overflow: 'hidden',
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr
                    style={{
                      background: 'linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(118, 75, 162) 100%)',
                    }}
                  >
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '12px 14px',
                        fontSize: '12px',
                        color: '#f8fafc',
                        fontWeight: 600,
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                        verticalAlign: 'middle',
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <FiShield size={15} strokeWidth={2} style={{ opacity: 0.95 }} aria-hidden />
                        Role
                      </span>
                    </th>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '12px 14px',
                        fontSize: '12px',
                        color: '#f8fafc',
                        fontWeight: 600,
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                        verticalAlign: 'middle',
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <FiUsers size={15} strokeWidth={2} style={{ opacity: 0.95 }} aria-hidden />
                        Member
                      </span>
                    </th>
                    <th
                      style={{
                        textAlign: 'center',
                        padding: '12px 14px',
                        fontSize: '12px',
                        color: '#f8fafc',
                        fontWeight: 600,
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                        verticalAlign: 'middle',
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%' }}>
                        <FiUser size={15} strokeWidth={2} style={{ opacity: 0.95 }} aria-hidden />
                        Show All Employees
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {teamMembers.map((row, index) => {
                    const RoleIcon = getRoleIconComponent(row.role);
                    return (
                      <tr key={row.role} style={{ borderTop: '1px solid #e5e7eb' }}>
                        <td
                          style={{
                            padding: '12px 14px',
                            borderRight: '1px solid #e5e7eb',
                            fontSize: '13px',
                            color: '#334155',
                            verticalAlign: 'middle',
                          }}
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                            <RoleIcon size={17} strokeWidth={2} style={{ color: TEAM_MEMBER_MODAL_ACCENT, flexShrink: 0 }} aria-hidden />
                            {row.role}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: '10px 12px',
                            borderRight: '1px solid #e5e7eb',
                            verticalAlign: 'middle',
                          }}
                        >
                          <StyledSelectDropdown
                            placeholder="Select member"
                            emptyOptionLabel={
                              loadingUsers ? 'Loading users...' : 'Select member'
                            }
                            value={row.memberId}
                            onChange={(v) => handleMemberChange(index, v)}
                            options={getMembersForRole(row.role, row.showAllEmployees).map(
                              (user) => ({
                                value: getUserValue(user),
                                label: getMemberLabelWithRating(user, row.role),
                              })
                            )}
                            disabled={loadingUsers || rosterControlsLocked}
                            isLoading={loadingUsers}
                            loadingText="Loading users..."
                          />
                        </td>
                        <td style={{ textAlign: 'center', padding: '12px 14px', verticalAlign: 'middle' }}>
                          <input
                            type="checkbox"
                            checked={row.showAllEmployees}
                            onChange={(e) =>
                              handleShowAllEmployeesChange(index, e.target.checked)
                            }
                            disabled={rosterControlsLocked}
                            style={{
                              width: '18px',
                              height: '18px',
                              accentColor: TEAM_MEMBER_MODAL_ACCENT,
                              cursor: rosterControlsLocked ? 'not-allowed' : 'pointer',
                              opacity: rosterControlsLocked ? 0.55 : 1,
                            }}
                            aria-label={`Show all employees for ${row.role}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div
            style={{
              padding: '0 20px 20px',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              alignItems: 'center',
            }}
          >
            <button
              type="button"
              onClick={() => navigate(`${appBasePath}/teams-events`)}
              style={{
                ...ADD_TEAM_FORM_CANCEL_BTN,
                flex: '0 0 auto',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                ...ADD_TEAM_FORM_PRIMARY_BTN,
                background: PRIMARY,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.8 : 1,
              }}
            >
              {!isSubmitting && !isEditMode && (
                <FiPlus size={15} strokeWidth={2.5} aria-hidden style={{ color: '#fff' }} />
              )}
              {isSubmitting
                ? isEditMode
                  ? 'Updating...'
                  : 'Saving...'
                : isEditMode
                ? 'Update'
                : 'Save Team'}
            </button>
          </div>
        </form>
      </div>

      <ConfirmActionModal
        isOpen={showScheduleConflictWarning}
        onClose={handleScheduleConflictClose}
        onConfirm={handleScheduleConflictConfirm}
        title="Schedule overlap"
        message={scheduleConflictModal.message}
        confirmText={scheduleConflictModal.pendingPayload ? 'Save anyway' : 'OK'}
        cancelText={scheduleConflictModal.pendingPayload ? 'Go back' : 'Close'}
        variant="primary"
        confirmColor="#f59e0b"
        loading={isSubmitting}
      />
      <ConfirmActionModal
        isOpen={showDuplicateMemberWarning}
        onClose={() => {
          setShowDuplicateMemberWarning(false);
          setDuplicateRoleAssignments([]);
        }}
        onConfirm={handleConfirmDuplicateMemberWarning}
        title="Duplicate Member Assignment"
        message={
          duplicateRoleAssignments.length
            ? `The same member is selected for multiple roles: ${duplicateRoleAssignments
                .map(({ memberId, roles }) => {
                  const user = getUserById(memberId);
                  const memberName = user ? getUserLabel(user) : 'Selected member';
                  return `${memberName} (${roles.join(', ')})`;
                })
                .join('; ')}. Do you want to continue?`
            : 'The same member is selected for multiple roles. Do you want to continue?'
        }
        confirmText={isEditMode ? 'Update Anyway' : 'Save Anyway'}
        cancelText="Review Assignments"
        variant="primary"
        confirmColor="#f59e0b"
        loading={isSubmitting}
      />
    </div>
  );
};

export default AddTeamEventPage;
